import { Injectable, inject } from "@angular/core";

import { ProductStock } from "@hermes/api-model-product";
import { Context, LOCALE, Settings, getBuildVersion } from "@hermes/app-core";
import { getStockTypeAndDisplayOnly } from "@hermes/fragments/product-utils";
import { Locale } from "@hermes/locale";
import { UserStateService } from "@hermes/states/user";
import {
    GTMEcommerceItemData,
    NO,
    PRODUCT_IN_STOCK,
    PRODUCT_OUT_OF_STOCK,
    YES,
} from "@hermes/fragments/analytics";

import {
    BRAND,
    SCENE7_HERMES_PRODUCT_REGEX,
} from "@hermes/utils-generic/constants";
import { getMatchAtIndex } from "@hermes/utils-generic/helpers";

import {
    MAX_CHUNK_SIZE,
    FACET_GRID,
    FACET_COMBINED_GRID,
    FACET_GRID_REDIRECTED,
    PRODUCT_GRID,
    SEARCH_GRID,
} from "../constants/grid.constant";
import { PageEventData } from "../events/page-event-data";

import { ProductInfo, ProductLoaded } from "../model/product-loaded.type";

import { getDimension63, hasStockRetailOnly } from "./grid-product.helper";

@Injectable()
export class GTMHelper {
    private locale = inject(LOCALE);
    private context = inject(Context);
    private userService = inject(UserStateService);
    private settings = inject(Settings);

    public formatPageEvent(
        apolloEvents: ProductLoaded[],
        pageViewType: string,
    ): PageEventData[] {
        if (apolloEvents.length === 0) {
            return [];
        }

        // Split apolloEvents into n tab of MAX_CHUNK_SIZE of apolloEvents
        const chunkedApolloEvents = [];
        for (
            let currentChunkStart = 0;
            currentChunkStart < apolloEvents.length;
            currentChunkStart += MAX_CHUNK_SIZE
        ) {
            chunkedApolloEvents.push(
                apolloEvents.slice(
                    currentChunkStart,
                    currentChunkStart + MAX_CHUNK_SIZE,
                ),
            );
        }

        return chunkedApolloEvents.map((events: ProductLoaded[]) => {
            const productInfos: ProductInfo[] = events.map(
                (apolloEvent) => apolloEvent.productInfo,
            );
            return {
                productInfos,
                menuLevel: events[0].menuLevel,
                isSearch: events[0].isSearchGrid,
                user: this.userService.getUser(),
                pagetype: pageViewType,
                languagecode: this.locale.langCode,
                countrycode: this.locale.countryCode,
                pageTranslate: this.getUrlTranslated(),
                departmentcode: this.getDepartmentCode(
                    productInfos,
                    events[0].isSearchGrid,
                ),
                impressions: this.getGTMEcommerceItemData(
                    productInfos,
                    pageViewType,
                ),
                buildVersion: getBuildVersion(this.settings.envKey),
            };
        });
    }

    /**
     * @param productInfos Array of products displayed in the grid
     * @param isSearch True if it's a search result
     * @returns the department code if we're not on a search grid
     */
    private getDepartmentCode(
        productInfos: ProductInfo[],
        isSearch: boolean,
    ): string | undefined {
        if (isSearch) {
            return "";
        }

        // Return first valid departmentCode
        return productInfos
            .map((productInfo) => productInfo.product.departmentCode)
            .find(Boolean);
    }

    /**
     * @param productInfos List of products display on the viewport
     * @param pagetype Page type (grid, search or facet)
     * @returns a list of product formatted for analytics
     */
    private getGTMEcommerceItemData(
        productInfos: ProductInfo[],
        pageViewType: string,
    ): GTMEcommerceItemData[] {
        return productInfos
            .map(
                ({ product, index }) =>
                    ({
                        name: product.title,
                        id: product.sku,
                        price: product.price ? product.price.toFixed(2) : "",
                        brand: BRAND,
                        category: `${product.departmentCode}/${product.familyCode}`,
                        variant: product.color || "N/A",
                        // eslint-disable-next-line unicorn/explicit-length-check
                        dimension5: product.size || "N/A",
                        dimension6: product.departmentCode,
                        dimension7: product.familyCode,
                        dimension8: product.productCode,
                        dimension9: product.divisionCode,
                        // dimension10 is a parameter for products of type "LOOK", need to be updated when look products will be done in angular
                        dimension10: "N/A",
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        in_store_only: String(hasStockRetailOnly(product)),
                        dimension37: product.stock.ecom
                            ? PRODUCT_IN_STOCK
                            : PRODUCT_OUT_OF_STOCK,
                        dimension52:
                            product.isEngravable || product.personalize
                                ? YES
                                : NO,
                        position: index + 1,
                        dimension53: getStockTypeAndDisplayOnly(
                            product.stock as ProductStock,
                            product.displayMode,
                        ),
                        list: pageViewType,
                        dimension62: product?.assets?.[0].url.includes(
                            "composite",
                        )
                            ? ""
                            : getMatchAtIndex(
                                  product?.assets?.[0].url,
                                  SCENE7_HERMES_PRODUCT_REGEX,
                                  1,
                              ),
                        dimension63: getDimension63(product),
                        vto_available:
                            product.isVto || product.vtoType !== undefined
                                ? YES
                                : NO,
                        quickbuy_available: product.quickBuyProperties?.eligible
                            ? YES
                            : NO,
                        fragrance_finder: "no",
                    }) as GTMEcommerceItemData,
            )
            .sort((productA, productB) => {
                if (!productA?.position) {
                    return -1;
                }
                if (!productB?.position) {
                    return 1;
                }
                return productA.position - productB.position;
            });
    }

    /**
     * Get the url in english
     */
    private getUrlTranslated(): string {
        if (this.context.isInBrowserMode()) {
            // get the first english link generated by Drupal with the translated link
            // eslint-disable-next-line unicorn/prefer-spread
            const link = Array.from(document.getElementsByTagName("link")).find(
                (element: HTMLLinkElement) => element.hreflang.includes("en-"),
            );

            if (link) {
                // if the link exist, we delete the url part (https://hermes.com/COUNTRY_CODE/LANGUAGE_CODE/)
                const arrayLink = link.href.split("/").slice(5);

                return arrayLink.join("/");
            }
        }

        return "";
    }
}

export const getPageViewType = (missingFacet?: boolean) => ({
    facet: FACET_GRID,
    facets: FACET_COMBINED_GRID,
    category: missingFacet ? FACET_GRID_REDIRECTED : PRODUCT_GRID,
    search: SEARCH_GRID,
});
